/**
 * AC-M5-3 — Artifacts slide-in sheet
 *
 * Shows generated artifacts (direction=generated) for a session.
 * Includes a Download button that opens the presigned attachment URL.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { formatDate } from '@/lib/localeFormatters';
import { FileText, FileSpreadsheet, Download, X, Package } from 'lucide-react';
import { t } from '@/lib/i18n';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { fetchJson } from '@/services/roros/base-roros.service';
import { agentSessionsService } from '@/services/roros/agent-sessions.service';
import type { SessionArtifact } from '@/services/roros/agent-sessions.service';

export type { SessionArtifact };

// ─── ArtifactsSheet ───────────────────────────────────────────────────────────

interface ArtifactsSheetProps {
  sessionId: string;
  onClose: () => void;
}

export function ArtifactsSheet({ sessionId, onClose }: ArtifactsSheetProps) {
  const [artifacts, setArtifacts] = useState<SessionArtifact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const loadArtifacts = useCallback(async () => {
    setIsLoading(true);
    try {
      const arts = await agentSessionsService.getArtifacts(sessionId);
      setArtifacts(arts);
    } catch {
      setArtifacts([]);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    void loadArtifacts();
  }, [loadArtifacts]);

  const handleDownload = useCallback(async (artifact: SessionArtifact) => {
    setDownloadingId(artifact.id);
    try {
      // fetchJson unwraps the .data envelope from the attachments endpoint
      const result = await fetchJson<{ url: string }>(`/api/attachments/${artifact.attachment_id}`);
      const url = result?.url;
      if (url) {
        window.open(url, '_blank');
      }
    } catch {
      // non-fatal
    } finally {
      setDownloadingId(null);
    }
  }, []);

  function ArtifactIcon({ type }: { type: string | null }) {
    if (type === 'csv') return <FileSpreadsheet className="h-4 w-4 text-green-600" />;
    return <FileText className="h-4 w-4 text-blue-600" />;
  }

  function TypeBadge({ type }: { type: string | null }) {
    if (type === 'csv') {
      return <Badge variant="success" className="text-xs">{t('CSV')}</Badge>;
    }
    return <Badge variant="default" className="text-xs">{t('Markdown')}</Badge>;
  }

  return (
    // Slide-in overlay
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="artifacts-sheet">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/20"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Sheet panel */}
      <div className="relative w-80 bg-white shadow-2xl flex flex-col h-full border-l border-slate-200 z-10">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-purple-500" />
            <h2 className="font-semibold text-sm text-slate-800">
              {t('Generated Artifacts')}
            </h2>
            {!isLoading && (
              <span className="text-xs text-slate-400">({artifacts.length})</span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label={t('Close artifacts panel')}
            data-testid="artifacts-sheet-close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden p-3 space-y-2">
          {isLoading && (
            <div className="flex justify-center py-8">
              <div className="animate-pulse text-xs text-slate-400">{t('Loading…')}</div>
            </div>
          )}
          {!isLoading && artifacts.length === 0 && (
            <div className="text-center py-8 text-sm text-slate-400" data-testid="artifacts-empty">
              {t('No artifacts generated yet.')}
            </div>
          )}
          {artifacts.map((artifact) => (
            <div
              key={artifact.id}
              className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 hover:border-slate-200 hover:bg-slate-50 transition-colors"
              data-testid="artifact-row"
            >
              <div className="flex-shrink-0 mt-0.5">
                <ArtifactIcon type={artifact.artifact_type} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{artifact.title}</p>
                <div className="flex items-center gap-2 mt-1">
                  <TypeBadge type={artifact.artifact_type} />
                  <span className="text-xs text-slate-400">
                    {formatDate(artifact.created_at)}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void handleDownload(artifact)}
                disabled={downloadingId === artifact.id}
                aria-label={t('Download')}
                data-testid="artifact-download-button"
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── ArtifactsBadgeButton ─────────────────────────────────────────────────────
// Shown in the session header. Only visible when count > 0.

interface ArtifactsBadgeButtonProps {
  sessionId: string;
  count: number;
  onClick: () => void;
}

export function ArtifactsBadgeButton({ count, onClick }: ArtifactsBadgeButtonProps) {
  if (count === 0) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      className="text-purple-600 hover:text-purple-800 hover:bg-purple-50 text-xs"
      data-testid="artifacts-badge-button"
    >
      <Package className="h-3.5 w-3.5 mr-1" />
      {t('Artifacts')} ({count})
    </Button>
  );
}
